// ========================================
// UNIFIED ORDER FORM V3
// Universal order form that adapts based on order type
// Single component for material, heat_treatment, and coating orders
// ========================================

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  Divider,
  IconButton,
  Alert,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  LinearProgress,
  Autocomplete
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  PictureAsPdf as PdfIcon,
  Upload as UploadIcon,
  AttachFile as AttachFileIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';
import { useNotification } from '../../contexts/NotificationContext';
import { unifiedOrderService } from '../../services/unifiedOrderService';
import { MaterialFields, HeatTreatmentFields, CoatingFields, ToolFields, CarbideFields } from './TypeSpecificFields';
import {
  CreateOrderRequest,
  MaterialData,
  HeatTreatmentData,
  CoatingData,
  ToolData,
  CarbideData
} from '../../types/unifiedOrderTypes';
import { formatDate, formatDateForInput } from '../../utils/dateUtils';

// Supplier interface with company_name for proper display
interface Supplier {
  id: string;
  name: string;
  company_name?: string;
  email?: string;
  phone?: string;
  address?: string;
}

interface UnifiedOrderFormProps {
  defaultOrderType?: 'material' | 'heat_treatment' | 'coating' | 'tool' | 'carbide';
}

const UnifiedOrderForm: React.FC<UnifiedOrderFormProps> = ({ defaultOrderType = 'material' }) => {
  const { showNotification } = useNotification();
  
  // Form state
  const [orderType, setOrderType] = useState<'material' | 'heat_treatment' | 'coating' | 'tool' | 'carbide'>(defaultOrderType);
  const [supplierId, setSupplierId] = useState<string>('');
  const [orderDate, setOrderDate] = useState<string>(formatDateForInput(new Date()));
  const [dueDate, setDueDate] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [positions, setPositions] = useState<Omit<CreateOrderRequest['positions'][0], 'id' | 'order_id' | 'delivered_quantity' | 'delivery_status'>[]>([
    {
      quantity: 0,
      part_name: '',
      part_number: '',
      unit_price: undefined,
      total_price: undefined,
      currency: 'EUR',
      notes: '',
      type_specific_data: getDefaultTypeSpecificData(defaultOrderType)
    }
  ]);
  
    // Position-based file upload state
  const [positionFiles, setPositionFiles] = useState<Map<number, File[]>>(new Map());
  
  // Auto-fill suggestions state
  const [previousPartNames, setPreviousPartNames] = useState<string[]>([]);
  const [previousPartNumbers, setPreviousPartNumbers] = useState<string[]>([]);
  
  // Loading and data state
  const [fileUploadDialogOpen, setFileUploadDialogOpen] = useState<boolean>(false);
  const [selectedPositionIndex, setSelectedPositionIndex] = useState<number | null>(null);
  const [uploadingFile, setUploadingFile] = useState<boolean>(false);
  
  // UI state
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [lastCreatedOrderId, setLastCreatedOrderId] = useState<string | null>(null);
  
  // State to track which field is being actively edited to prevent circular calculations
  const [activelyEditingField, setActivelyEditingField] = useState<{positionIndex: number, field: 'unit_price' | 'total_price' | 'quantity' | null}>({positionIndex: -1, field: null});
  
  // PDF language selection state
  const [selectedLanguage, setSelectedLanguage] = useState<'de' | 'en'>('de');

  // ========================================

  // Load suppliers on component mount
  useEffect(() => {
    loadSuppliers();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset positions when order type changes
  useEffect(() => {
    setPositions([{
      quantity: 0,
      part_name: '',
      part_number: '',
      unit_price: undefined,
      total_price: undefined,
      currency: 'EUR',
      notes: '',
      type_specific_data: getDefaultTypeSpecificData(orderType)
    }]);
    setPositionFiles(new Map());
  }, [orderType]);

  // Load previous values from localStorage for auto-fill
  useEffect(() => {
    const savedPartNames = localStorage.getItem('wzf_previous_part_names');
    const savedPartNumbers = localStorage.getItem('wzf_previous_part_numbers');
    
    if (savedPartNames) {
      setPreviousPartNames(JSON.parse(savedPartNames));
    }
    
    if (savedPartNumbers) {
      setPreviousPartNumbers(JSON.parse(savedPartNumbers));
    }
  }, []);

  // ========================================
  // DATA LOADING
  // ========================================

  const loadSuppliers = async () => {
    try {
      setLoading(true);
      const response = await unifiedOrderService.getSuppliers();
      if (response.success && response.data) {
        setSuppliers(response.data);
      }
    } catch (error: any) {
      showNotification('Fehler beim Laden der Lieferanten: ' + error.message, 'error'); // Error loading suppliers: ${error.message}
    } finally {
      setLoading(false);
    }
  };

  // ========================================
  // FORM HELPERS
  // ========================================

  function getDefaultTypeSpecificData(type: 'material' | 'heat_treatment' | 'coating' | 'tool' | 'carbide'): MaterialData | HeatTreatmentData | CoatingData | ToolData | CarbideData {
    switch (type) {
      case 'material':
        return { form: 'Round' } as MaterialData;
      case 'heat_treatment':
        return { treatment_type: 'Vacuum-Hardening' } as HeatTreatmentData;
      case 'coating':
        return { treatment_type: 'PVD', coating_spec: '', steel_quality: '' } as CoatingData;
      case 'tool':
        return { 
          manufacturer_article_number: '', 
          tool_type: '', 
          manufacturer: '', 
          additional_info: '' 
        } as ToolData;
      case 'carbide':
        return { material_spec: '' } as CarbideData;
    }
  }

  const getOrderTypeDisplayName = () => {
    switch (orderType) {
      case 'material': return 'Materialbestellung'; // Material Order
      case 'heat_treatment': return 'Wärmebehandlungsauftrag'; // Heat Treatment Order
      case 'coating': return 'Beschichtungsauftrag'; // Coating Order
      case 'tool': return 'Werkzeugbestellung'; // Tool Order
      case 'carbide': return 'Hartmetallbestellung'; // Carbide Order
    }
  };

  // ========================================
  // POSITION MANAGEMENT
  // ========================================

  const addPosition = () => {
    const newPosition = {
      quantity: 0,
      part_name: '',
      part_number: '',
      unit_price: undefined,
      total_price: undefined,
      currency: 'EUR',
      notes: '',
      type_specific_data: getDefaultTypeSpecificData(orderType)
    };
    setPositions([...positions, newPosition]);
  };

  const removePosition = (index: number) => {
    if (positions.length === 1) {
      showNotification('Mindestens eine Position ist erforderlich', 'warning'); // At least one position is required
      return;
    }
    
    const newPositions = positions.filter((_, i) => i !== index);
    setPositions(newPositions);
    
    // Also remove any files for this position
    const newFiles = new Map(positionFiles);
    newFiles.delete(index);
    // Re-index remaining files
    const reindexedFiles = new Map();
    newFiles.forEach((files, posIndex) => {
      if (posIndex > index) {
        reindexedFiles.set(posIndex - 1, files);
      } else {
        reindexedFiles.set(posIndex, files);
      }
    });
    setPositionFiles(reindexedFiles);
  };

  const updatePosition = useCallback((index: number, field: string, value: any) => {
    const newPositions = [...positions];
    newPositions[index] = { ...newPositions[index], [field]: value };
    setPositions(newPositions);
    
    // Save to localStorage for auto-fill suggestions
    if (field === 'part_name' && value && !previousPartNames.includes(value)) {
      const updated = [...previousPartNames, value];
      setPreviousPartNames(updated);
      localStorage.setItem('wzf_previous_part_names', JSON.stringify(updated));
    } else if (field === 'part_number' && value && !previousPartNumbers.includes(value)) {
      const updated = [...previousPartNumbers, value];
      setPreviousPartNumbers(updated);
      localStorage.setItem('wzf_previous_part_numbers', JSON.stringify(updated));
    }
  }, [positions, previousPartNames, previousPartNumbers]);

  // ========================================
  // INPUT HANDLERS WITH PROPER STATE ACCESS
  // ========================================

  const handleUnitPriceChange = useCallback((index: number, rawValue: string) => {
    // Get current position from state, not from closure
    const currentPosition = positions[index];
    
    console.log('🔍 [DEBUG] Unit price input (callback):', {
      rawValue,
      parsedValue: Number(rawValue),
      isEmpty: rawValue === '',
      positionIndex: index,
      currentQuantity: currentPosition.quantity,
      currentUnitPrice: currentPosition.unit_price,
      currentTotalPrice: currentPosition.total_price,
      activelyEditingField: activelyEditingField
    });
    
    if (rawValue === '') {
      updatePosition(index, 'unit_price', undefined);
      return;
    }
    
    const unitPrice = Number(rawValue);
    if (!isNaN(unitPrice)) {
      updatePosition(index, 'unit_price', unitPrice);
      
      // NO AUTO-CALCULATION ON KEYSTROKE - only update the value
      console.log('🔍 [DEBUG] Unit price updated, no auto-calculation during typing');
    }
  }, [updatePosition, positions]);

  const handleUnitPriceBlur = useCallback((index: number) => {
    // Get current position from state at blur time
    const currentPosition = positions[index];
    
    console.log('🔍 [DEBUG] Unit price blur - calculating total price:', {
      positionIndex: index,
      currentQuantity: currentPosition.quantity,
      currentUnitPrice: currentPosition.unit_price,
      currentTotalPrice: currentPosition.total_price
    });
    
    // Only auto-calculate total price if quantity exists and unit price is set
    if (currentPosition.quantity && currentPosition.quantity > 0 && 
        currentPosition.unit_price !== undefined && currentPosition.unit_price > 0) {
      const totalPrice = currentPosition.unit_price * currentPosition.quantity;
      console.log('🔍 [DEBUG] Auto-calculating total price on blur:', { 
        unitPrice: currentPosition.unit_price, 
        quantity: currentPosition.quantity, 
        totalPrice 
      });
      updatePosition(index, 'total_price', totalPrice);
    }
    
    // Clear the actively editing field
    setActivelyEditingField({ positionIndex: -1, field: null });
  }, [updatePosition, positions]);

  const handleTotalPriceChange = useCallback((index: number, rawValue: string) => {
    // Get current position from state, not from closure
    const currentPosition = positions[index];
    
    console.log('🔍 [DEBUG] Total price input (callback):', {
      rawValue,
      parsedValue: Number(rawValue),
      isEmpty: rawValue === '',
      positionIndex: index,
      currentQuantity: currentPosition.quantity,
      currentUnitPrice: currentPosition.unit_price,
      currentTotalPrice: currentPosition.total_price,
      activelyEditingField: activelyEditingField
    });
    
    if (rawValue === '') {
      updatePosition(index, 'total_price', undefined);
      return;
    }
    
    const totalPrice = Number(rawValue);
    if (!isNaN(totalPrice)) {
      updatePosition(index, 'total_price', totalPrice);
      
      // NO AUTO-CALCULATION ON KEYSTROKE - only update the value
      console.log('🔍 [DEBUG] Total price updated, no auto-calculation during typing');
    }
  }, [updatePosition, positions]);

  const handleTotalPriceBlur = useCallback((index: number) => {
    // Get current position from state at blur time
    const currentPosition = positions[index];
    
    console.log('🔍 [DEBUG] Total price blur - calculating unit price:', {
      positionIndex: index,
      currentQuantity: currentPosition.quantity,
      currentUnitPrice: currentPosition.unit_price,
      currentTotalPrice: currentPosition.total_price
    });
    
    // Only auto-calculate unit price if quantity exists and total price is set
    if (currentPosition.quantity && currentPosition.quantity > 0 && 
        currentPosition.total_price !== undefined && currentPosition.total_price > 0) {
      const unitPrice = currentPosition.total_price / currentPosition.quantity;
      console.log('🔍 [DEBUG] Auto-calculating unit price on blur:', { 
        totalPrice: currentPosition.total_price, 
        quantity: currentPosition.quantity, 
        unitPrice 
      });
      updatePosition(index, 'unit_price', unitPrice);
    }
    
    // Clear the actively editing field
    setActivelyEditingField({ positionIndex: -1, field: null });
  }, [updatePosition, positions]);

  const updatePositionTypeSpecificData = (index: number, data: MaterialData | HeatTreatmentData | CoatingData | ToolData | CarbideData) => {
    const newPositions = [...positions];
    newPositions[index] = { ...newPositions[index], type_specific_data: data };
    setPositions(newPositions);
  };

  // ========================================
  // FILE UPLOAD MANAGEMENT
  // ========================================

  const handleFileUpload = (positionIndex: number) => {
    setSelectedPositionIndex(positionIndex);
    setFileUploadDialogOpen(true);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || selectedPositionIndex === null) return;

    console.log('🔍 [DEBUG] File selection event:', {
      selectedPositionIndex,
      filesCount: files.length,
      fileNames: Array.from(files).map(f => f.name)
    });

    const currentFiles = positionFiles.get(selectedPositionIndex) || [];
    const newFiles = [...currentFiles, ...Array.from(files)];
    
    const updatedFiles = new Map(positionFiles);
    updatedFiles.set(selectedPositionIndex, newFiles);
    setPositionFiles(updatedFiles);
    
    console.log('🔍 [DEBUG] Updated position files map:', {
      positionIndex: selectedPositionIndex,
      newFilesCount: newFiles.length,
      totalMapSize: updatedFiles.size,
      allEntries: Array.from(updatedFiles.entries())
    });
    
    setFileUploadDialogOpen(false);
    setSelectedPositionIndex(null);
    
    showNotification(`${files.length} Datei(en) für Position ${selectedPositionIndex + 1} hinzugefügt`, 'success');
  };

  const handleFileDelete = (positionIndex: number, fileIndex: number) => {
    const currentFiles = positionFiles.get(positionIndex) || [];
    const newFiles = currentFiles.filter((_, index) => index !== fileIndex);
    
    const updatedFiles = new Map(positionFiles);
    if (newFiles.length === 0) {
      updatedFiles.delete(positionIndex);
    } else {
      updatedFiles.set(positionIndex, newFiles);
    }
    setPositionFiles(updatedFiles);
    
    showNotification('Datei entfernt', 'success');
  };

  const getFileIconAndColor = (fileName: string) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf':
        return { icon: <PdfIcon />, color: 'error' as const };
      case 'dwg':
      case 'dxf':
        return { icon: <DescriptionIcon />, color: 'primary' as const };
      case 'jpg':
      case 'jpeg':
      case 'png':
        return { icon: <DescriptionIcon />, color: 'info' as const };
      default:
        return { icon: <AttachFileIcon />, color: 'default' as const };
    }
  };

  // ========================================
  // FORM VALIDATION
  // ========================================

  const validateForm = (): string | null => {
    if (!supplierId) return 'Bitte wählen Sie einen Lieferanten aus'; // Please select a supplier
    if (!orderDate) return 'Bitte wählen Sie ein Bestelldatum'; // Please select an order date
    if (positions.length === 0) return 'Mindestens eine Position ist erforderlich'; // At least one position is required
    
    for (let i = 0; i < positions.length; i++) {
      const pos = positions[i];
      if (!pos.quantity || pos.quantity < 1) return `Position ${i + 1}: Bitte Menge eingeben (min. 1)`; // Position ${i + 1}: Please enter quantity (min. 1)
      
      // Validate type-specific data
      if (orderType === 'coating') {
        const coatingData = pos.type_specific_data as CoatingData;
        if (!coatingData.coating_spec) return `Position ${i + 1}: Beschichtungsspezifikation ist erforderlich`; // Position ${i + 1}: Coating specification is required
        if (!coatingData.steel_quality) return `Position ${i + 1}: Stahlqualität ist erforderlich`; // Position ${i + 1}: Steel quality is required
      }
      
      if (orderType === 'tool') {
        const toolData = pos.type_specific_data as ToolData;
        if (!toolData.manufacturer_article_number) return `Position ${i + 1}: Hersteller-Artikelnummer ist erforderlich`; // Position ${i + 1}: Manufacturer article number is required
        if (!toolData.tool_type) return `Position ${i + 1}: Werkzeugtyp ist erforderlich`; // Position ${i + 1}: Tool type is required
        if (!toolData.manufacturer) return `Position ${i + 1}: Hersteller ist erforderlich`; // Position ${i + 1}: Manufacturer is required
        if (!toolData.additional_info) return `Position ${i + 1}: Zusätzliche Informationen sind erforderlich`; // Position ${i + 1}: Additional information is required
      }
      
      if (orderType === 'carbide') {
        const carbideData = pos.type_specific_data as CarbideData;
        if (!carbideData.material_spec) return `Position ${i + 1}: Materialspezifikation ist erforderlich`; // Position ${i + 1}: Material specification is required
      }
    }
    
    return null;
  };

  // ========================================
  // FORM SUBMISSION
  // ========================================

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const validationError = validateForm();
    if (validationError) {
      showNotification(validationError, 'error');
      return;
    }

    try {
      setSubmitting(true);
      
      const orderData: CreateOrderRequest = {
        order_type: orderType,
        supplier_id: supplierId,
        order_date: orderDate,
        due_date: dueDate || undefined,
        notes: notes || undefined,
        positions
      };

      console.log('🔍 [DEBUG] About to create order with data:', orderData);
      console.log('🔍 [DEBUG] Position files map size:', positionFiles.size);
      console.log('🔍 [DEBUG] Position files contents:', Array.from(positionFiles.entries()));

      const response = await unifiedOrderService.createOrder(orderData);
      
      if (response.success && response.data) {
        const createdOrderId = response.data.orderId;
        setLastCreatedOrderId(createdOrderId);
        
        console.log('🔍 [DEBUG] Order created successfully:', createdOrderId);
        
        // Upload position files if any exist
        let fileUploadErrors = 0;
        if (positionFiles.size > 0) {
          console.log('🔍 [DEBUG] Starting file upload process...');
          showNotification('Bestellung erstellt! Lade Positionsdateien hoch...', 'info'); // Order created! Uploading position files...
          
          // Use Array.from to handle Map iteration
          const fileEntries = Array.from(positionFiles.entries());
          console.log('🔍 [DEBUG] File entries to process:', fileEntries);
          
          for (const [positionIndex, files] of fileEntries) {
            if (files.length > 0) {
              try {
                const positionId = `${createdOrderId}-pos-${positionIndex + 1}`;
                console.log(`🔍 [DEBUG] Uploading ${files.length} files for position ${positionIndex + 1} (ID: ${positionId})`);
                console.log('🔍 [DEBUG] Files to upload:', files.map(f => ({ name: f.name, size: f.size, type: f.type })));
                
                const uploadResponse = await unifiedOrderService.uploadPositionFiles(createdOrderId, positionId, files);
                console.log(`🔍 [DEBUG] Upload response for position ${positionIndex + 1}:`, uploadResponse);
              } catch (error: any) {
                console.error(`🔍 [DEBUG] Error uploading files for position ${positionIndex + 1}:`, error);
                fileUploadErrors++;
              }
            } else {
              console.log(`🔍 [DEBUG] No files to upload for position ${positionIndex + 1}`);
            }
          }
        } else {
          console.log('🔍 [DEBUG] No position files to upload (positionFiles.size = 0)');
        }

        if (fileUploadErrors > 0) {
          showNotification(`${getOrderTypeDisplayName()} erstellt, aber ${fileUploadErrors} Datei-Upload(s) fehlgeschlagen`, 'warning');
        } else {
          showNotification(`${getOrderTypeDisplayName()} erfolgreich erstellt!`, 'success'); // ${getOrderTypeDisplayName()} created successfully!
        }

        // NOTE: Removed automatic PDF generation to allow manual editing of order before finalization
        // PDF can be generated manually using the "PDF generieren" button after order creation
        
        // Reset form
        resetForm();
      } else {
        showNotification(response.error || 'Fehler beim Erstellen der Bestellung', 'error'); // Failed to create order
      }
    } catch (error: any) {
      console.error('🔍 [DEBUG] Error creating order:', error);
      showNotification('Fehler beim Erstellen der Bestellung: ' + error.message, 'error'); // Error creating order: ${error.message}
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setSupplierId('');
    setOrderDate(formatDateForInput(new Date()));
    setDueDate('');
    setNotes('');
    setPositions([{
      quantity: 0,
      part_name: '',
      part_number: '',
      unit_price: undefined,
      total_price: undefined,
      currency: 'EUR',
      notes: '',
      type_specific_data: getDefaultTypeSpecificData(orderType)
    }]);
    setPositionFiles(new Map());
  };

  // ========================================
  // QUICK ACTIONS FOR LAST CREATED ORDER
  // ========================================

  const handleFinalizeLastOrder = async () => {
    if (!lastCreatedOrderId) return;
    
    try {
      const response = await unifiedOrderService.finalizeOrder(lastCreatedOrderId, selectedLanguage);
      if (response.success) {
        showNotification(`Bestellung finalisiert und PDF generiert! (${selectedLanguage.toUpperCase()})`, 'success'); // Order finalized and PDF generated! (LANGUAGE)
      }
    } catch (error: any) {
      showNotification('Fehler beim Finalisieren der Bestellung: ' + error.message, 'error'); // Error finalizing order: ${error.message}
    }
  };

  // ========================================
  // RENDER
  // ========================================

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Neue Bestellung erstellen // Create New Order
      </Typography>

      {/* Last Created Order Quick Actions */}
      {lastCreatedOrderId && (
        <Alert 
          severity="success" 
          sx={{ mb: 3 }}
          action={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ mr: 1 }}>
                PDF Sprache:
              </Typography>
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <Select
                  value={selectedLanguage}
                  onChange={(e) => setSelectedLanguage(e.target.value as 'de' | 'en')}
                  sx={{ fontSize: '0.75rem', height: '32px' }}
                  displayEmpty
                >
                  <MenuItem value="de">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      🇩🇪 DE
                    </Box>
                  </MenuItem>
                  <MenuItem value="en">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      🇬🇧 EN
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
              <Button 
                size="small" 
                startIcon={<PdfIcon />}
                onClick={handleFinalizeLastOrder}
                sx={{ mr: 1 }}
                variant="contained"
                color="primary"
              >
                PDF generieren // Generate PDF
              </Button>
              <Button 
                size="small" 
                onClick={() => setLastCreatedOrderId(null)}
              >
                Schließen // Dismiss
              </Button>
            </Box>
          }
        >
          Bestellung erfolgreich erstellt! ID: {lastCreatedOrderId} {/* Order created successfully! ID: {lastCreatedOrderId} */}
        </Alert>
      )}

      <Card>
        <CardContent>
          <form onSubmit={handleSubmit}>
            {/* Order Type Selection */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Box>
                <FormControl fullWidth>
                  <InputLabel>Bestellungstyp {/* Order Type */}</InputLabel>
                  <Select
                    value={orderType}
                    onChange={(e) => setOrderType(e.target.value as any)}
                    label="Bestellungstyp" // Order Type
                  >
                    <MenuItem value="material">
                      <Chip label="Materialbestellung" color="primary" size="small" sx={{ mr: 1 }} /> {/* Material Order */}
                      Materialbestellung {/* Material Order */}
                    </MenuItem>
                    <MenuItem value="heat_treatment">
                      <Chip label="Wärmebehandlung" color="secondary" size="small" sx={{ mr: 1 }} /> {/* Heat Treatment */}
                      Wärmebehandlungsauftrag {/* Heat Treatment Order */}
                    </MenuItem>
                    <MenuItem value="coating">
                      <Chip label="Beschichtung" color="info" size="small" sx={{ mr: 1 }} /> {/* Coating */}
                      Beschichtungsauftrag {/* Coating Order */}
                    </MenuItem>
                    <MenuItem value="tool">
                      <Chip label="Werkzeug" color="warning" size="small" sx={{ mr: 1 }} /> {/* Tool */}
                      Werkzeugbestellung {/* Tool Order */}
                    </MenuItem>
                    <MenuItem value="carbide">
                      <Chip label="Hartmetall" color="error" size="small" sx={{ mr: 1 }} /> {/* Carbide */}
                      Hartmetallbestellung {/* Carbide Order */}
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* Language Selection for PDF */}
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
                <FormControl sx={{ flex: '0 0 200px' }}>
                  <InputLabel>PDF Sprache {/* PDF Language */}</InputLabel>
                  <Select
                    value={selectedLanguage}
                    onChange={(e) => setSelectedLanguage(e.target.value as 'de' | 'en')}
                    label="PDF Sprache" // PDF Language
                  >
                    <MenuItem value="de">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        🇩🇪 Deutsch
                      </Box>
                    </MenuItem>
                    <MenuItem value="en">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        🇬🇧 English
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* Supplier Selection */}
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
                <FormControl sx={{ flex: 1 }} required>
                  <InputLabel>Lieferant {/* Supplier */}</InputLabel>
                  <Select
                    value={supplierId}
                    onChange={(e) => setSupplierId(e.target.value)}
                    label="Lieferant" // Supplier
                  >
                    {suppliers.map((supplier) => (
                      <MenuItem key={supplier.id} value={supplier.id}>
                        {supplier.company_name ? `${supplier.name} - ${supplier.company_name}` : supplier.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* Order Date and Due Date */}
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
                <TextField
                  sx={{ flex: 1 }}
                  required
                  label="Bestelldatum" // Order Date
                  type="date"
                  value={orderDate}
                  onChange={(e) => setOrderDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />

                <TextField
                  sx={{ flex: 1 }}
                  label="Liefertermin" // Due Date
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>

              {/* Notes */}
              <Box>
                <TextField
                  fullWidth
                  label="Bestellnotizen" // Order Notes
                  multiline
                  rows={2}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Zusätzliche Notizen zu dieser Bestellung..." // Additional notes for this order...
                />
              </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Positions */}
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  Bestellpositionen ({positions.length}) {/* Order Positions ({positions.length}) */}
                </Typography>
                <Button
                  startIcon={<AddIcon />}
                  onClick={addPosition}
                  variant="outlined"
                  size="small"
                >
                  Position hinzufügen {/* Add Position */}
                </Button>
              </Box>

              {positions.map((position, index) => (
                <Paper key={index} sx={{ p: 3, mb: 2, backgroundColor: '#fafafa' }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6">
                      Position {index + 1}
                    </Typography>
                    {positions.length > 1 && (
                      <IconButton
                        onClick={() => removePosition(index)}
                        color="error"
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {/* Basic Position Fields */}
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
                      <TextField
                        sx={{ flex: '0 0 200px' }}
                        required
                        label="Menge" // Quantity
                        type="number"
                        placeholder="z.B. 10"
                        value={position.quantity === 0 ? '' : position.quantity}
                        onFocus={(e) => {
                          setActivelyEditingField({ positionIndex: index, field: 'quantity' });
                          e.target.select();
                        }}
                        onBlur={() => {
                          // Auto-recalculate prices when quantity field is finished (on blur)
                          const quantity = position.quantity;
                          console.log('🔍 [DEBUG] Quantity blur - checking for price recalculation:', {
                            quantity,
                            unitPrice: position.unit_price,
                            totalPrice: position.total_price
                          });
                          
                          if (quantity > 0) {
                            if (position.unit_price && position.unit_price > 0) {
                              const totalPrice = position.unit_price * quantity;
                              console.log('🔍 [DEBUG] Quantity blur - recalculating total price:', { unitPrice: position.unit_price, quantity, totalPrice });
                              updatePosition(index, 'total_price', totalPrice);
                            } else if (position.total_price && position.total_price > 0) {
                              const unitPrice = position.total_price / quantity;
                              console.log('🔍 [DEBUG] Quantity blur - recalculating unit price:', { totalPrice: position.total_price, quantity, unitPrice });
                              updatePosition(index, 'unit_price', unitPrice);
                            }
                          }
                          
                          setActivelyEditingField({ positionIndex: -1, field: null });
                        }}
                        onChange={(e) => {
                          const quantity = Number(e.target.value) || 0;
                          console.log('🔍 [DEBUG] Quantity input:', {
                            rawValue: e.target.value,
                            parsedQuantity: quantity,
                            positionIndex: index,
                            currentUnitPrice: position.unit_price,
                            currentTotalPrice: position.total_price,
                            activelyEditingField: activelyEditingField
                          });
                          
                          updatePosition(index, 'quantity', quantity);
                          
                          // NO AUTO-CALCULATION ON KEYSTROKE - only update the value
                          console.log('🔍 [DEBUG] Quantity updated, no auto-calculation during typing');
                        }}
                        inputProps={{ min: 1 }}
                      />
                    
                      <Autocomplete
                        freeSolo
                        options={previousPartNames}
                        value={position.part_name || ''}
                        onInputChange={(_, value) => updatePosition(index, 'part_name', value || '')}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            sx={{ flex: 1 }}
                            label="Teilename" // Part Name
                            placeholder="Name oder Beschreibung des Teils" // Name or description of the part
                            helperText="Vorschläge basierend auf früheren Eingaben" // Suggestions based on previous entries
                          />
                        )}
                      />
                      
                      <Autocomplete
                        freeSolo
                        options={previousPartNumbers}
                        value={position.part_number || ''}
                        onInputChange={(_, value) => updatePosition(index, 'part_number', value || '')}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            sx={{ flex: 1 }}
                            label="Teilenummer" // Part Number
                            placeholder="Interne Teilenummer" // Internal part number
                            helperText="Vorschläge basierend auf früheren Eingaben" // Suggestions based on previous entries
                          />
                        )}
                      />
                    </Box>
                    
                    {/* Optional Pricing Fields */}
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
                      <TextField
                        sx={{ flex: '0 0 200px' }}
                        label="Stückpreis (optional)" // Unit Price (optional)
                        type="number"
                        placeholder="z.B. 15.50"
                        value={position.unit_price === undefined ? '' : position.unit_price}
                        onFocus={(e) => {
                          setActivelyEditingField({ positionIndex: index, field: 'unit_price' });
                          e.target.select();
                        }}
                        onBlur={() => handleUnitPriceBlur(index)}
                        onChange={(e) => handleUnitPriceChange(index, e.target.value)}
                        inputProps={{ min: 0, step: 0.01 }}
                        helperText="Preis pro Stück"
                      />
                      
                      <TextField
                        sx={{ flex: '0 0 200px' }}
                        label="Gesamtpreis (optional)" // Total Price (optional)
                        type="number"
                        placeholder="z.B. 775.00"
                        value={position.total_price === undefined ? '' : position.total_price}
                        onFocus={(e) => {
                          setActivelyEditingField({ positionIndex: index, field: 'total_price' });
                          e.target.select();
                        }}
                        onBlur={() => handleTotalPriceBlur(index)}
                        onChange={(e) => handleTotalPriceChange(index, e.target.value)}
                        inputProps={{ min: 0, step: 0.01 }}
                        helperText="Gesamtpreis für Position"
                      />
                      
                      <TextField
                        sx={{ flex: '0 0 150px' }}
                        label="Währung" // Currency
                        value={position.currency || 'EUR'}
                        onChange={(e) => updatePosition(index, 'currency', e.target.value)}
                        placeholder="EUR"
                        helperText="Standard: EUR"
                      />
                    </Box>
                    
                    <Box>
                      <TextField
                        fullWidth
                        label="Positionsnotizen" // Position Notes
                        value={position.notes || ''}
                        onChange={(e) => updatePosition(index, 'notes', e.target.value)}
                        placeholder="Notizen spezifisch für diese Position" // Notes specific to this position
                      />
                    </Box>

                    {/* Type-Specific Fields */}
                    <Box>
                      <Divider sx={{ my: 2 }} />
                      {orderType === 'material' && (
                        <MaterialFields
                          data={position.type_specific_data as MaterialData}
                          onChange={(data) => updatePositionTypeSpecificData(index, data)}
                        />
                      )}
                      {orderType === 'heat_treatment' && (
                        <HeatTreatmentFields
                          data={position.type_specific_data as HeatTreatmentData}
                          onChange={(data) => updatePositionTypeSpecificData(index, data)}
                        />
                      )}
                      {orderType === 'coating' && (
                        <CoatingFields
                          data={position.type_specific_data as CoatingData}
                          onChange={(data) => updatePositionTypeSpecificData(index, data)}
                        />
                      )}
                      {orderType === 'tool' && (
                        <ToolFields
                          data={position.type_specific_data as ToolData}
                          onChange={(data) => updatePositionTypeSpecificData(index, data)}
                        />
                      )}
                      {orderType === 'carbide' && (
                        <CarbideFields
                          data={position.type_specific_data as CarbideData}
                          onChange={(data) => updatePositionTypeSpecificData(index, data)}
                        />
                      )}
                    </Box>

                    {/* File Uploads */}
                    <Box>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle1" gutterBottom>
                        Dateien hochladen {/* Upload Files */}
                      </Typography>

                      <Button
                        variant="outlined"
                        onClick={() => handleFileUpload(index)}
                        size="small"
                        startIcon={<UploadIcon />}
                        fullWidth
                      >
                        Datei auswählen {/* Select File */}
                      </Button>

                      {positionFiles.get(index) && positionFiles.get(index)!.length > 0 && (
                        <Box mt={2}>
                          {positionFiles.get(index)!.map((file: File, fileIndex: number) => {
                            const { icon, color } = getFileIconAndColor(file.name);
                            return (
                              <Chip
                                key={fileIndex}
                                icon={icon}
                                label={`${file.name} (${(file.size / 1024).toFixed(1)} KB)`}
                                onDelete={() => handleFileDelete(index, fileIndex)}
                                deleteIcon={<DeleteIcon />}
                                color={color}
                                sx={{ mr: 1, mb: 1 }}
                              />
                            );
                          })}
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Paper>
              ))}
            </Box>

            {/* Submit Button */}
            <Box display="flex" justifyContent="space-between" mt={3}>
              <Button
                type="button"
                variant="outlined"
                onClick={resetForm}
                disabled={submitting}
              >
                Formular zurücksetzen {/* Reset Form */}
              </Button>
              
              <Button
                type="submit"
                variant="contained"
                startIcon={submitting ? <CircularProgress size={20} /> : <SaveIcon />}
                disabled={submitting}
                size="large"
              >
                {submitting ? 'Bestellung wird erstellt...' : `${getOrderTypeDisplayName()} erstellen`} {/* Creating Order... : Create ${getOrderTypeDisplayName()} */}
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>

      {/* File Upload Dialog */}
      <Dialog 
        open={fileUploadDialogOpen} 
        onClose={() => setFileUploadDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Datei für Position {selectedPositionIndex !== null ? selectedPositionIndex + 1 : ''} hochladen
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Unterstützte Dateiformate: PDF, DWG, DXF, JPG, PNG (max. 10MB)
            </Typography>
            <Button
              variant="outlined"
              component="label"
              fullWidth
              startIcon={<UploadIcon />}
              sx={{ mt: 2 }}
            >
              Datei auswählen
              <input
                type="file"
                hidden
                accept=".pdf,.dwg,.dxf,.jpg,.jpeg,.png"
                multiple
                onChange={handleFileChange}
              />
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFileUploadDialogOpen(false)}>
            Abbrechen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UnifiedOrderForm;
